import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../api';

const API_BASE = `${API_BASE_URL}/api/v1/seritonex`;

/**
 * Custom hook for managing wishlist functionality
 * Ready to integrate with backend API endpoints
 */
export const useWishlist = (currentUser, isLoggedIn) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stable ref so callbacks don't depend on currentUser identity
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const getVariantId = useCallback((value) => {
    if (value == null) return 0;
    if (typeof value === 'number' || typeof value === 'string') {
      return parseInt(value, 10) || 0;
    }
    return (
      value.variantId ||
      value.variant_id ||
      value.product_variant_id ||
      value.id ||
      0
    );
  }, []);

  // Load wishlist from API
  const loadWishlist = useCallback(async () => {
    const user = currentUserRef.current;
    if (!user) return;

    setLoading(true);
    try {
      // Get user_id from sessionStorage (set during login)
      const storedUserId = sessionStorage.getItem('userId');
      const userId = storedUserId || user?.uid || user?.user_id || 0;
      
      // Get token from sessionStorage
      const token = sessionStorage.getItem('authToken');
      
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE}/wishlist/user-wishlist`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          user_id: parseInt(userId) || 0,
          product_variant_id: 0
        }) 
      });
      
      if (response.ok) {
        const data = await response.json();
        // Transform API response to match UI expectations
        const items = (Array.isArray(data) ? data : data.items || []).map(item => ({
          id: item.product_variant_id,
          productId: item.product_id,
          title: item.title,
          name: item.title,
          description: item.description,
          price: parseFloat(item.value_incl) || 0,
          image: item.image_path || (item.images && item.images[0]?.path) || '',
          category: item.category_description,
          subCategory: item.sub_category_description,
          sku: item.sku,
          barcode: item.barcode,
          weight: item.weight,
          stock: item.quantity_on_hand,
          images: item.images || []
        }));
        setWishlistItems(items);
      } else {
        setWishlistItems([]);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  }, []); // stable — reads currentUserRef inside

  // Load wishlist when the user identity changes
  const userId = currentUser?.uid || currentUser?.user_id;
  useEffect(() => {
    if (isLoggedIn && userId) {
      loadWishlist();
    } else {
      setWishlistItems([]);
    }
  }, [userId, isLoggedIn, loadWishlist]);

  // Add item to wishlist
  const addToWishlist = useCallback(async (product) => {
    const user = currentUserRef.current;
    if (!user) {
      throw new Error('User must be logged in to add to wishlist');
    }

    const storedUserId = sessionStorage.getItem('userId');
    const userId = storedUserId || user?.uid || user?.user_id;
    const token = sessionStorage.getItem('authToken');

    if (!userId || !token) {
      throw new Error('Missing authentication credentials — please log in again.');
    }

    const productVariantId = getVariantId(product);
    if (!productVariantId) {
      throw new Error('Invalid product variant ID');
    }

    try {
      const response = await fetch(`${API_BASE}/wishlist/add-variant`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: parseInt(userId),
          product_variant_id: productVariantId
        })
      });

      if (response.ok) {
        await loadWishlist();
        return { success: true };
      } else {
        const errorBody = await response.text().catch(() => '');
        console.error('Wishlist add-variant failed:', { status: response.status, body: errorBody, userId, productVariantId });
        throw new Error(`Failed to add to wishlist (${response.status}): ${errorBody || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  }, [getVariantId, loadWishlist]);

  // Remove item from wishlist
  const removeFromWishlist = useCallback(async (productId) => {
    const user = currentUserRef.current;
    if (!user) {
      throw new Error('User must be logged in');
    }

    const storedUserId = sessionStorage.getItem('userId');
    const userId = storedUserId || user?.uid || user?.user_id;
    const token = sessionStorage.getItem('authToken');

    if (!userId || !token) {
      throw new Error('Missing authentication credentials — please log in again.');
    }

    try {
      const response = await fetch(`${API_BASE}/wishlist/delete-variant`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: parseInt(userId),
          product_variant_id: productId || 0
        })
      });

      if (response.ok) {
        await loadWishlist();
        return { success: true };
      } else {
        const errorBody = await response.text().catch(() => '');
        console.error('Wishlist delete-variant failed:', { status: response.status, body: errorBody });
        throw new Error(`Failed to remove from wishlist (${response.status})`);
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  }, [loadWishlist]);

  // Check if item is in wishlist
  const isInWishlist = useCallback((productId) => {
    const targetId = getVariantId(productId);
    return wishlistItems.some((item) => getVariantId(item) === targetId);
  }, [getVariantId, wishlistItems]);

  // Toggle add/remove for wishlist buttons
  const toggleWishlist = useCallback(async (productOrVariantId) => {
    const variantId = getVariantId(productOrVariantId);
    if (!variantId) {
      throw new Error('Invalid product variant id');
    }

    if (isInWishlist(variantId)) {
      return removeFromWishlist(variantId);
    }

    return addToWishlist({ product_variant_id: variantId });
  }, [addToWishlist, getVariantId, isInWishlist, removeFromWishlist]);

  // Clear entire wishlist
  const clearWishlist = useCallback(async () => {
    const user = currentUserRef.current;
    if (!user) {
      throw new Error('User must be logged in');
    }

    const token = sessionStorage.getItem('authToken') || '';
    const userId = user.uid || sessionStorage.getItem('userId');

    try {
      const response = await fetch(`${API_BASE}/wishlist/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ user_id: userId }),
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        throw new Error(`Failed to clear wishlist (${response.status}): ${errorBody || 'Unknown error'}`);
      }

      setWishlistItems([]);
      localStorage.removeItem(`wishlist_${userId}`);
      return { success: true };
    } catch (error) {
      console.error('Error clearing wishlist — local state preserved. Retry to clear server-side:', error);
      // Re-throw so callers can display an error message; local state is NOT cleared on failure
      throw error;
    }
  }, []);

  return {
    wishlistItems,
    loading,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearWishlist,
    refreshWishlist: loadWishlist,
    refetch: loadWishlist
  };
};

export default useWishlist;
